import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import { authenticate } from "./store/session";
import { startSessionTimeout } from "./utils/sessionTimeout";
import { startSessionValidation, SESSION_INVALIDATED_EVENT, SESSION_TIMEOUT_EVENT } from "./utils/sessionValidator";
import SessionTimeoutWarning from "./components/SessionTimeoutWarning";
import SessionInvalidatedModal from "./components/SessionInvalidatedModal";
import ChangePasswordModal from "./components/ChangePasswordModal";
import Navbar from "./components/navbar/Navbar";
import useTimeoutSettings from "./hooks/useTimeoutSettings";

import LoadTrailer from "./pages/trailer/loadTrailer/LoadTrailer";
import LoadTruck from "./pages/truck/LoadTruck";
import LoadBuilding from "./pages/building/LoadBuilding";
import LoadYard from "./pages/yard/LoadYard";
import UpdateTrailer from "./pages/trailer/updateTrailer/UpdateTrailer";
import LoginFormPage from "./components/LoginFormPage";
import Home from "./pages/home/Home";
import TrailerDetail from "./pages/trailer/trailerDetail/TrailerDetail";
import AdminDashboard from "./pages/dashboard/AdminDash";
import SignupFormPage from "./components/SignupFormPage";
import TruckDetail from "./pages/truck/TruckDetail";
import BuildingDetail from "./pages/building/BuildingDetail";
import YardDetail from "./pages/yard/YardDetail";
import UserDetail from "./pages/user/UserDetail";
import UpdateTruck from "./pages/truck/UpdateTruck";
import UpdateBuilding from "./pages/building/UpdateBuilding";
import UpdateYard from "./pages/yard/UpdateYard";
import EditUserForm from "./components/EditUserForm";
import AddTruckPage from "./components/AddTruckPage";
import AddTrailerPage from "./components/trailers/addTrailer/AddTrailerPage";
import BulkUploadTrailers from "./pages/trailer/addTrailer/BulkUploadTrailers";
import AuditLogs from "./pages/auditLogs/AuditLogs";
import HowTo from "./pages/howto/HowTo";
// import TruckList from "./pages/lists/TruckList";
// import TrailerList from "./pages/lists/TrailerList";
import AddYardPage from './components/AddYardPage';
import AddBuildingPage from './components/AddBuildingPage';
import Settings from './components/settings/Settings';


function App() {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const [invalidationMessage, setInvalidationMessage] = useState('');
  const sessionUser = useSelector((state) => state.session.user);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Use our custom hook for timeout settings
  const { timeoutHours } = useTimeoutSettings();

  useEffect(() => {
    dispatch(authenticate())
      .then(() => setIsLoaded(true))
      .catch(() => {
        setIsLoaded(true);
      });
  }, [dispatch]);

  // Set up session timeout when user is logged in
  useEffect(() => {
    let cleanupTimeout;
    let cleanupValidator;

    if (sessionUser) {
      // Start session timeout with the current timeout hours
      const timeoutMilliseconds = timeoutHours * 60 * 60 * 1000;
      console.log(`Setting session timeout to ${timeoutHours} hours (${timeoutMilliseconds} ms)`);
      cleanupTimeout = startSessionTimeout(timeoutMilliseconds);

      // Start session validation to check for multiple logins
      cleanupValidator = startSessionValidation();

      // Listen for session invalidation events
      const handleSessionInvalidated = (event) => {
        console.log('Session invalidation event received:', event.detail);
        setInvalidationMessage(event.detail.message);
        setSessionInvalidated(true);
      };

      // Listen for session timeout events
      const handleSessionTimeout = (event) => {
        console.log('Session timeout event received:', event.detail);
        setInvalidationMessage(event.detail.message);
        setSessionInvalidated(true);
      };

      window.addEventListener(SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
      window.addEventListener(SESSION_TIMEOUT_EVENT, handleSessionTimeout);

      // Clean up event listeners
      return () => {
        if (cleanupTimeout) {
          cleanupTimeout();
        }
        if (cleanupValidator) {
          cleanupValidator();
        }
        window.removeEventListener(SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
        window.removeEventListener(SESSION_TIMEOUT_EVENT, handleSessionTimeout);
      };
    }

    return () => {
      if (cleanupTimeout) {
        cleanupTimeout();
      }
      if (cleanupValidator) {
        cleanupValidator();
      }
    };
  }, [sessionUser, timeoutHours]);

  // Check if user needs to change password on first login
  useEffect(() => {
    if (sessionUser && sessionUser.first_login) {
      setShowPasswordModal(true);
    }
  }, [sessionUser]);

  if (!isLoaded) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-3xl">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {sessionUser && <Navbar />}
      {showTimeoutWarning && (
        <SessionTimeoutWarning
          onContinue={() => setShowTimeoutWarning(false)}
        />
      )}
      {sessionInvalidated && (
        <SessionInvalidatedModal
          message={invalidationMessage}
          onClose={() => setSessionInvalidated(false)}
        />
      )}
      {showPasswordModal && sessionUser && sessionUser.first_login && (
        <ChangePasswordModal
          onClose={() => setShowPasswordModal(false)}
        />
      )}
      <Switch>
        <Route exact path="/login"
          render={() => {
            if (sessionUser) {
              return sessionUser.is_admin ? (
                <Redirect to="/admin/dashboard" />
              ) : (
                <Redirect to="/" />
              );
            }
            return <LoginFormPage />;
          }}
        />

        <Route exact path="/"
          render={() => (sessionUser ? <Home /> : <Redirect to="/login" />)}
        />

        <Route path="/signup">
          {sessionUser?.is_super_manager || sessionUser?.is_admin ? (
            <SignupFormPage />
          ) : (
            <Redirect to="/login" />
          )}
        </Route>

        <Route exact path="/admin/dashboard"
          render={() => (sessionUser?.is_admin ? <AdminDashboard /> : <Redirect to="/" />)}
        />
        <Route path="/audit-logs"
          render={() => (sessionUser?.is_admin ? <AuditLogs /> : <Redirect to="/login" />)}
        />

        {/* User Routes */}
        <Route path="/users/:id/edit"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager ? <EditUserForm {...props} /> : <Home />)}
        />
        <Route exact path="/users/:id"
          render={(props) => (sessionUser ? <UserDetail {...props} /> : <Redirect to="/login" />)}
        />

        {/* Trailer Routes */}
        <Route path="/trailers/new"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager ? <AddTrailerPage {...props} /> : <Redirect to="/login" />)}
        />
        <Route path="/trailers/bulk-upload"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager ? <BulkUploadTrailers {...props} /> : <Redirect to="/login" />)}
        />
        <Route
          path="/trailers/:id/edit"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager  ? <UpdateTrailer {...props} /> : <Redirect to="/login" />)}
        />
        <Route
          exact
          path="/trailers/:id"
          render={(props) => (sessionUser ? <TrailerDetail {...props} /> : <Redirect to="/login" />)}
        />
        {/* <Route
          path="/trailers"
          render={(props) => (sessionUser ? <TrailerList {...props} /> : <Redirect to="/login" />)}
        /> */}

        {/* Truck Routes */}
        <Route path="/trucks/new"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager  ? <AddTruckPage {...props} /> : <Redirect to="/login" />)}
        />
        <Route path="/trucks/:id/edit"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager ? <UpdateTruck {...props} /> : <Redirect to="/login" />)}
        />
        <Route exact path="/trucks/:id"
          render={(props) => (sessionUser ? <TruckDetail {...props} /> : <Redirect to="/login" />)}
        />
        {/* <Route
          path="/trucks"
          render={(props) => (sessionUser ? <TruckList {...props} /> : <Redirect to="/login" />)}
        /> */}

        {/* Building Routes */}
        <Route path="/buildings/new"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager ? <AddBuildingPage {...props} /> : <Redirect to="/login" />)}
        />
        <Route path="/buildings/:id/edit"
          render={() => (sessionUser?.is_admin || sessionUser?.is_super_manager  ? <UpdateBuilding /> : <Redirect to="/login" />)}
        />
        <Route exact path="/buildings/:id"
          render={(props) => (sessionUser ? <BuildingDetail {...props} /> : <Redirect to="/login" />)}
        />
        {/* <Route path="/buildings"
          render={() => (sessionUser ? <LoadBuilding /> : <Redirect to="/login" />)}
        /> */}

        {/* Yard Routes */}
        <Route path="/yards/new"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager ? <AddYardPage {...props} /> : <Redirect to="/login" />)}
        />
        <Route path="/yards/:id/edit"
          render={(props) => (sessionUser?.is_admin || sessionUser?.is_super_manager  ? <UpdateYard {...props} /> : <Redirect to="/login" />)}
        />
        <Route exact path="/yards/:id"
          render={(props) => (sessionUser ? <YardDetail {...props} /> : <Redirect to="/login" />)}
        />
        {/* <Route path="/yards"
          render={() => (sessionUser ? <LoadYard /> : <Redirect to="/login" />)}
        /> */}

        {/* User Guide Route */}

        <Route exact path="/how-to"
           render={() => (sessionUser ? <HowTo /> : <Redirect to="/login" />)}
        />

        {/* Settings Route - Admin Only */}
        <Route exact path="/settings"
           render={() => (sessionUser?.is_admin ? <Settings /> : <Redirect to="/" />)}
        />

        <Route path="*">
          <Redirect to="/login" />
        </Route>
      </Switch>
    </>
  );
}

export default App;
